import { useEffect } from 'react';
import { Outlet, useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import { ArrowLeft, RefreshCw } from 'lucide-react';

import { ProtectedShell } from '@/components/layout/protected-shell';
import { StagingBanner } from '@/components/layout/staging-banner';
import { AjudaDoErro } from '@/components/shared/AjudaDoErro';
import { AnelDeAtualizacao, ModuloQueFalta, PlugueSolto } from '@/components/shared/IlustracoesDeStatus';
import { StatusPage } from '@/components/shared/StatusPage';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { appConfig } from '@/lib/config';
import { MeProvider } from '@/lib/hooks/useMe';
import { Link } from '@/lib/nav';
import { HOME } from '@/route-access';

const isStaging = appConfig.appMode === 'staging';

/** Antigo `app/layout.tsx`: banner de staging e toasts. */
export function RootLayout() {
    return (
        <div className={isStaging ? 'pt-12' : undefined}>
            {isStaging && <StagingBanner />}
            <Outlet />
            <Toaster />
        </div>
    );
}

/** Antigo `app/(protected)/layout.tsx`: usuário carregado, sidebar e banners. */
export function ProtectedLayout() {
    return (
        <MeProvider>
            <ProtectedShell isStaging={isStaging}>
                <Outlet />
            </ProtectedShell>
        </MeProvider>
    );
}

function IrParaInicio({ variant }: { variant?: 'secondary' }) {
    return (
        <Button asChild variant={variant}>
            <Link href={HOME}>Ir para o início</Link>
        </Button>
    );
}

export function NotFoundPage() {
    return (
        <StatusPage
            art={<ModuloQueFalta />}
            eyebrow="Erro 404"
            title="Esta página não existe"
            description="O endereço pode ter mudado ou o link veio incompleto. Volte para onde estava ou comece pelo início."
            detalhe={
                <span className="mt-4 inline-block h-7 max-w-full truncate rounded-full leading-7 bg-fill-strong px-3 font-mono text-footnote text-label-secondary">
                    {window.location.pathname}
                </span>
            }
            actions={
                <>
                    {window.history.length > 1 && (
                        <Button variant="ghost" onClick={() => window.history.back()}>
                            <ArrowLeft /> Voltar
                        </Button>
                    )}
                    <IrParaInicio />
                </>
            }
        />
    );
}

/** Após um deploy, os pedaços (chunks) da versão antiga somem do servidor e a troca de página falha. */
const CHUNK_ANTIGO = /dynamically imported module|Importing a module script failed|Failed to fetch module/i;

export function ErrorPage({ error: bruto, reset }: ErrorComponentProps) {
    const error = bruto instanceof Error ? bruto : new Error(String(bruto));
    const router = useRouter();

    useEffect(() => {
        console.error(bruto);
    }, [bruto]);

    if (CHUNK_ANTIGO.test(error.message)) {
        return (
            <StatusPage
                art={<AnelDeAtualizacao />}
                eyebrow="Atualização"
                title="Saiu uma versão nova do sistema"
                description="Atualize a página para continuar de onde parou. O que já foi salvo continua lá."
                actions={
                    <Button onClick={() => window.location.reload()}>
                        <RefreshCw /> Atualizar página
                    </Button>
                }
            />
        );
    }

    const tentarDeNovo = () => {
        reset();
        void router.invalidate();
    };

    return (
        <StatusPage
            art={<PlugueSolto />}
            eyebrow="Erro inesperado"
            title="Não conseguimos abrir esta página"
            description="Costuma ser uma falha passageira. Tente de novo em alguns instantes."
            actions={
                <>
                    <IrParaInicio variant="secondary" />
                    <Button onClick={tentarDeNovo}>
                        <RefreshCw /> Tentar de novo
                    </Button>
                </>
            }
        >
            <AjudaDoErro error={error} />
        </StatusPage>
    );
}
